/**
 * Phone / tablet controls for the viewer: an on-screen joystick (move) and
 * touch gestures on the 3D view (one finger drags to look, two fingers pinch
 * to zoom). Both write into shared input objects that the player controllers
 * read every frame — the desktop avatar and the view-only camera — the same
 * way they read the keyboard.
 */
import React, { useEffect, useRef } from 'react';
import { useThree } from '@react-three/fiber';
import './touchControls.css';

export const isTouchDevice = typeof window !== 'undefined' && (
  window.matchMedia?.('(pointer: coarse)').matches || 'ontouchstart' in window || navigator.maxTouchPoints > 0
) && !window.matchMedia?.('(pointer: fine)').matches;

/** Joystick: x = strafe right, z = forward (each −1..1). */
export const touchMove = { x: 0, z: 0 };
/** Look drag (px) and pinch zoom accumulated since the last frame; the controller reads and clears them. */
export const touchLook = { dx: 0, dy: 0, zoom: 0 };

/** Read and clear the pending look / zoom. */
export function takeTouchLook() {
  const out = { dx: touchLook.dx, dy: touchLook.dy, zoom: touchLook.zoom };
  touchLook.dx = touchLook.dy = touchLook.zoom = 0;
  return out;
}

/** Joystick magnitude 0..1 (with a small dead zone), or 0 when untouched. */
export function touchMoveAmount() {
  const m = Math.min(1, Math.hypot(touchMove.x, touchMove.z));
  return m < 0.12 ? 0 : m;
}

const RADIUS = 44; // max knob travel (px)

/** The on-screen joystick (bottom left). */
export function TouchJoystick() {
  const baseRef = useRef<HTMLDivElement>(null);
  const knobRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const base = baseRef.current;
    if (!base) return;
    let id: number | null = null;
    const update = (x: number, y: number) => {
      const r = base.getBoundingClientRect();
      let dx = x - (r.left + r.width / 2), dy = y - (r.top + r.height / 2);
      const d = Math.hypot(dx, dy);
      if (d > RADIUS) { dx = (dx / d) * RADIUS; dy = (dy / d) * RADIUS; }
      if (knobRef.current) knobRef.current.style.transform = `translate(${dx}px, ${dy}px)`;
      touchMove.x = dx / RADIUS;
      touchMove.z = -dy / RADIUS;
    };
    const reset = () => {
      id = null; touchMove.x = touchMove.z = 0;
      if (knobRef.current) knobRef.current.style.transform = 'translate(0px, 0px)';
      base.classList.remove('active');
    };
    const start = (e: TouchEvent) => {
      e.preventDefault(); e.stopPropagation();
      if (id !== null) return;
      const t = e.changedTouches[0]; id = t.identifier;
      base.classList.add('active');
      update(t.clientX, t.clientY);
    };
    const move = (e: TouchEvent) => {
      e.preventDefault(); e.stopPropagation();
      for (const t of Array.from(e.changedTouches)) if (t.identifier === id) update(t.clientX, t.clientY);
    };
    const end = (e: TouchEvent) => {
      e.stopPropagation();
      for (const t of Array.from(e.changedTouches)) if (t.identifier === id) reset();
    };
    const noMenu = (e: Event) => e.preventDefault();
    base.addEventListener('touchstart', start, { passive: false });
    base.addEventListener('touchmove', move, { passive: false });
    base.addEventListener('touchend', end);
    base.addEventListener('touchcancel', end);
    base.addEventListener('contextmenu', noMenu);
    return () => {
      base.removeEventListener('touchstart', start);
      base.removeEventListener('touchmove', move);
      base.removeEventListener('touchend', end);
      base.removeEventListener('touchcancel', end);
      base.removeEventListener('contextmenu', noMenu);
      reset();
    };
  }, []);
  return <div ref={baseRef} className="gx-touch-joystick" aria-label="Move">
    <div ref={knobRef} className="gx-touch-joystick-knob" />
  </div>;
}

/** Touch look / pinch zoom on the 3D canvas (mount inside the Canvas). */
export function TouchLook() {
  const { gl } = useThree();
  useEffect(() => {
    const el = gl.domElement;
    let last: { x: number; y: number } | null = null;
    let pinch = 0;
    const spread = (t: TouchList) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const start = (e: TouchEvent) => {
      if (e.touches.length === 1) { last = { x: e.touches[0].clientX, y: e.touches[0].clientY }; pinch = 0; }
      else if (e.touches.length === 2) { last = null; pinch = spread(e.touches); }
    };
    const move = (e: TouchEvent) => {
      if (e.touches.length === 1 && last) {
        const t = e.touches[0];
        touchLook.dx += t.clientX - last.x;
        touchLook.dy += t.clientY - last.y;
        last = { x: t.clientX, y: t.clientY };
      } else if (e.touches.length === 2 && pinch) {
        const s = spread(e.touches);
        touchLook.zoom += (pinch - s) * 0.03; // fingers apart = zoom in
        pinch = s;
      }
      e.preventDefault(); // no page scroll / browser zoom while steering
    };
    const end = (e: TouchEvent) => {
      if (e.touches.length === 0) { last = null; pinch = 0; }
      else if (e.touches.length === 1) { last = { x: e.touches[0].clientX, y: e.touches[0].clientY }; pinch = 0; }
    };
    el.style.touchAction = 'none';
    el.addEventListener('touchstart', start, { passive: true });
    el.addEventListener('touchmove', move, { passive: false });
    el.addEventListener('touchend', end);
    el.addEventListener('touchcancel', end);
    return () => {
      el.removeEventListener('touchstart', start);
      el.removeEventListener('touchmove', move);
      el.removeEventListener('touchend', end);
      el.removeEventListener('touchcancel', end);
    };
  }, [gl]);
  return null;
}
